import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useQuiz } from '../context/QuizContext';

const resultContent = {
  terra: {
    emoji: '🌱',
    title: 'TERRA',
    color: 'green',
    bgColor: 'bg-green-900/20',
    borderColor: 'border-green-600',
    textColor: 'text-green-400',
    accentColor: '#4a7c59',
    description: 'Neste momento, seu Elemento Terra está em instabilidade ativa e já está impactando sua segurança interna, decisões e prosperidade.',
    impactIntro: 'Você se esforça, mas sente que o chão some embaixo dos seus pés. Que por mais que tente, a estabilidade não se sustenta. Não é falta de esforço. É uma programação interna que ainda não foi liberada, e que continua operando em silêncio, sabotando o que você tenta construir.',
    impacts: [
      'Você se esforça muito e sente pouco retorno',
      'Dificuldade de manter dinheiro ou estabilidade',
      'Insegurança nas decisões',
      'Medo silencioso do futuro',
      'Sensação constante de recomeço'],
    consequenceIntro: 'Enquanto essa raiz não for tratada, os mesmos ciclos vão se repetir. O corpo acumula o que a mente não processa. E a vida continua pedindo mais de você enquanto você sente que tem cada vez menos para dar.',
    consequences: [
      'Ciclos de escassez',
      'Autossabotagem inconsciente',
      'Exaustão emocional',
      'Perda de confiança em si',
      'Vida em modo sobrevivência'],
    benefitIntro: 'Quando o Terra é liberado, você para de sobreviver e começa a construir. A segurança deixa de ser algo que você busca fora e passa a ser algo que você sente dentro.',
    benefits: [
      'Estabilidade emocional e financeira',
      'Decisões mais firmes',
      'Sensação real de segurança',
      'Capacidade de sustentar resultados',
      'Prosperidade com leveza']
  },
  agua: {
    emoji: '🌊',
    title: 'ÁGUA',
    color: 'blue',
    bgColor: 'bg-blue-900/20',
    borderColor: 'border-blue-500',
    textColor: 'text-blue-400',
    accentColor: '#3b6fa0',
    description: 'Neste momento, seu Elemento Água está em bloqueio ativo e já está impactando sua forma de sentir, se valorizar e se permitir receber.',
    impactIntro: 'Você duvida de si mesma com frequência. Sente que precisa ser melhor, fazer mais, provar que merece. Por baixo disso existe uma dor antiga que nunca foi nomeada, e que continua ditando o quanto você se permite receber.',
    impacts: [
      'Dúvida constante sobre si',
      'Mente acelerada',
      'Perfeccionismo que paralisa',
      'Dificuldade de confiar na intuição',
      'Sensação de não merecimento'],
    consequenceIntro: 'A autocobrança vai aumentando. A comparação vira companheira constante. E você continua buscando aprovação de fora porque ainda não aprendeu a se dar a sua própria.',
    consequences: [
      'Autocobrança excessiva',
      'Comparação constante',
      'Bloqueio emocional',
      'Insatisfação persistente'],
    benefitIntro: 'Quando o Água é liberado, você para de questionar se merece e começa a receber. A intuição volta a falar mais alto que o medo. E os relacionamentos começam a refletir o que você sente por dentro.',
    benefits: [
      'Leveza emocional',
      'Confiança na intuição',
      'Relações mais profundas',
      'Sensação real de merecimento']
  },
  fogo: {
    emoji: '🔥',
    title: 'FOGO',
    color: 'orange',
    bgColor: 'bg-orange-900/20',
    borderColor: 'border-orange-500',
    textColor: 'text-orange-400',
    accentColor: '#c2710c',
    description: 'Neste momento, seu Elemento Fogo está em bloqueio ativo e já está impactando sua ação, confiança e capacidade de gerar resultados.',
    impactIntro: 'Você sabe o que precisa fazer. Mas algo trava. A vontade existe, o entusiasmo começa e some. Não é preguiça. É uma chama que foi se apagando por dentro, muitas vezes porque você aprendeu que agir traz consequências que doem.',
    impacts: [
      'Você sabe o que fazer, mas não age',
      'Começa projetos e não conclui',
      'Entusiasmo que desaparece',
      'Falta de energia',
      'Sensação de estagnação'],
    consequenceIntro: 'A vida vai ficando suspensa. As oportunidades passam e você assiste de longe. E aquela sensação de que você ainda não começou o que veio fazer vai pesando cada vez mais.',
    consequences: [
      'Procrastinação constante',
      'Perda de oportunidades',
      'Baixa autoestima',
      'Vida travada'],
    benefitIntro: 'Quando o Fogo é liberado, a ação volta a vir de um lugar de força, não de obrigação. Você começa a concluir o que começa. E a confiança em si mesma passa a ser o combustível, não a aprovação dos outros.',
    benefits: [
      'Ação com confiança',
      'Energia para concluir tarefas',
      'Coragem para aproveitar oportunidades',
      'Resultados concretos']
  },
  ar: {
    emoji: '🌪️',
    title: 'AR',
    color: 'purple',
    bgColor: 'bg-purple-900/20',
    borderColor: 'border-purple-500',
    textColor: 'text-purple-400',
    accentColor: '#7c3aed',
    description: 'Neste momento, seu Elemento Ar está em instabilidade ativa e já está impactando sua clareza mental, sua leveza emocional e suas decisões.',
    impactIntro: 'Sua mente não descansa. Os pensamentos giram, as preocupações se acumulam e você sente que nunca consegue estar totalmente presente. É como viver com um ruído interno constante que não deixa você enxergar com clareza o que fazer.',
    impacts: [
      'Pensamentos acelerados e ansiedade constante',
      'Sensação de sobrecarga',
      'Dificuldade de aceitar mudanças',
      'Apego ao passado',
      'Medo de arriscar'],
    consequenceIntro: 'A ansiedade vai aumentando. As decisões ficam cada vez mais difíceis. E você começa a evitar o novo porque o que já conhece, mesmo que doa, pelo menos é previsível.',
    consequences: [
      'Ansiedade constante',
      'Relações tensas',
      'Oportunidades evitadas',
      'Autossabotagem'],
    benefitIntro: 'Quando o Ar é liberado, a mente encontra silêncio. As decisões ficam mais fáceis porque você aprende a confiar no que sente, não só no que pensa. E a leveza que você buscava fora começa a vir de dentro.',
    benefits: [
      'Clareza mental',
      'Leveza emocional',
      'Adaptação com fluidez',
      'Abertura para novas oportunidades']
  },
  nenhum_bloqueio: {
    emoji: '🌟',
    title: 'NENHUM BLOQUEIO IDENTIFICADO',
    description: 'Aparentemente, de acordo com suas respostas, não foram identificados bloqueios ativos nos 4 elementos.',
    // O número 4 é renderizado via JSX quando exibido na UI
    note: 'Porém: Se sua vida apresenta instabilidade física, mental, emocional, energética ou espiritual, podem existir bloqueios mais profundos que não foram revelados neste diagnóstico inicial.'
  },
  equilibrado: {
    emoji: '🌟',
    title: 'ELEMENTOS EQUILIBRADOS',
    description: 'Seus elementos estão equilibrados neste momento. Continue cultivando esse equilíbrio com práticas diárias.'
  }
};

export default function Result() {
  const navigate = useNavigate();
  const { resultado, calculatedResults, leadData } = useQuiz();

  const content = resultContent[resultado as keyof typeof resultContent] || resultContent.equilibrado;

  const elements = calculatedResults ? [
    { name: 'Terra', emoji: '🌱', data: { percentual: calculatedResults.terraPercentual, classificacao: calculatedResults.terraClassificacao }, borderColor: 'border-green-600', bgColor: 'bg-green-600', textColor: 'text-green-400', isMaiorBloqueio: calculatedResults.elementosMaiorBloqueio.includes('terra') },
    { name: 'Água', emoji: '🌊', data: { percentual: calculatedResults.aguaPercentual, classificacao: calculatedResults.aguaClassificacao }, borderColor: 'border-blue-500', bgColor: 'bg-blue-500', textColor: 'text-blue-400', isMaiorBloqueio: calculatedResults.elementosMaiorBloqueio.includes('agua') },
    { name: 'Fogo', emoji: '🔥', data: { percentual: calculatedResults.fogoPercentual, classificacao: calculatedResults.fogoClassificacao }, borderColor: 'border-orange-500', bgColor: 'bg-orange-500', textColor: 'text-orange-400', isMaiorBloqueio: calculatedResults.elementosMaiorBloqueio.includes('fogo') },
    { name: 'Ar', emoji: '🌪️', data: { percentual: calculatedResults.arPercentual, classificacao: calculatedResults.arClassificacao }, borderColor: 'border-purple-500', bgColor: 'bg-purple-500', textColor: 'text-purple-400', isMaiorBloqueio: calculatedResults.elementosMaiorBloqueio.includes('ar') }
  ] : [];

  return (
    <div className="reative-page min-h-screen py-8 md:py-12 px-4" style={{ backgroundColor: '#0d0d0f' }}>
      <div className="text-center mb-8"></div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-4xl mx-auto">

        {/* Título com nome personalizado */}
        <div className="text-center mb-6 md:mb-8 px-4">
          <h1 className="font-bold text-white mb-2" style={{ fontFamily: 'Georgia, serif', fontSize: 'clamp(24px, 6vw, 36px)' }}>
            Seu Resultado do Teste dos <span className="numerals">4</span> Elementos
          </h1>
          {leadData?.nome && (
            <p className="text-gray-400 italic" style={{ fontSize: 'clamp(18px, 4vw, 22px)' }}>
              {leadData.nome}
            </p>
          )}
        </div>

        {/* Visão Geral dos 4 Elementos */}
        <div className="rounded-3xl shadow-xl p-6 md:p-10 mb-6 md:mb-8 border border-gray-700" style={{ backgroundColor: '#16161a' }}>
          <h2 className="font-bold text-white text-center mb-6 md:mb-8" style={{ fontFamily: 'Georgia, serif', fontSize: 'clamp(20px, 5vw, 28px)' }}>
            Visão Geral dos Seus <span className="numerals">4</span> Elementos
          </h2>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {elements.map((element) => (
              <div key={element.name} className={`rounded-2xl p-4 md:p-5 shadow-md border ${element.isMaiorBloqueio ? 'border-amber-500 ring-2 ring-amber-500/40' : 'border-gray-700'}`} style={{ backgroundColor: element.isMaiorBloqueio ? '#1e1a10' : '#1c1c21' }}>
                <div className="text-center mb-3">
                  <span className="text-3xl md:text-4xl block mb-2">{element.emoji}</span>
                  <span className={`font-bold block mb-2 ${element.textColor}`} style={{ fontSize: 'clamp(17px, 3.5vw, 18px)' }}>{element.name}</span>
                  <span className="font-bold text-white block mb-1 numerals" style={{ fontSize: 'clamp(22px, 6vw, 30px)' }}>{element.data.percentual}%</span>
                  <span className="text-gray-400 block font-medium" style={{ fontSize: 'clamp(17px, 2.8vw, 18px)' }}>{element.data.classificacao}</span>
                  {element.isMaiorBloqueio && (
                    <span className="block mt-2 text-amber-400 font-bold" style={{ fontSize: 'clamp(20px, 3vw, 21px)' }}>MAIOR BLOQUEIO</span>
                  )}
                </div>
                <div className="w-full rounded-full h-2 overflow-hidden" style={{ backgroundColor: '#2a2a32' }}>
                  <div className={`h-full ${element.bgColor} transition-all duration-1000 rounded-full`} style={{ width: `${element.data.percentual}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* O que os elementos costumam indicar */}
        <div className="rounded-3xl shadow-xl p-6 md:p-10 mb-6 md:mb-8 border border-gray-700" style={{ backgroundColor: '#16161a' }}>
          <h3 className="font-bold text-white mb-6 text-center" style={{ fontFamily: 'Georgia, serif', fontSize: 'clamp(20px, 5vw, 28px)' }}>
            🔎 O Que os Elementos Bloqueados Costumam Indicar
          </h3>
          <div className="space-y-4 md:space-y-5">
            <div className="flex items-start gap-3 md:gap-4 p-4 rounded-xl border border-green-700/50" style={{ backgroundColor: '#0f1a12' }}>
              <span className="text-3xl md:text-4xl flex-shrink-0">🌱</span>
              <p className="text-gray-300 leading-relaxed">
                <span className="font-bold text-green-400">Terra:</span> escassez, medo do futuro, sobrecarga e dificuldade de sustentar resultados.
              </p>
            </div>
            <div className="flex items-start gap-3 md:gap-4 p-4 rounded-xl border border-blue-700/50" style={{ backgroundColor: '#0a1220' }}>
              <span className="text-3xl md:text-4xl flex-shrink-0">🌊</span>
              <p className="text-gray-300 leading-relaxed">
                <span className="font-bold text-blue-400">Água:</span> falta de merecimento, apatia emocional e dificuldade de confiar.
              </p>
            </div>
            <div className="flex items-start gap-3 md:gap-4 p-4 rounded-xl border border-orange-700/50" style={{ backgroundColor: '#1a0e00' }}>
              <span className="text-3xl md:text-4xl flex-shrink-0">🔥</span>
              <p className="text-gray-300 leading-relaxed">
                <span className="font-bold text-orange-400">Fogo:</span> procrastinação, desânimo e dificuldade de agir.
              </p>
            </div>
            <div className="flex items-start gap-3 md:gap-4 p-4 rounded-xl border border-purple-700/50" style={{ backgroundColor: '#120a1e' }}>
              <span className="text-3xl md:text-4xl flex-shrink-0">🌪️</span>
              <p className="text-gray-300 leading-relaxed">
                <span className="font-bold text-purple-400">Ar:</span> ansiedade, instabilidade emocional e apego ao passado.
              </p>
            </div>
          </div>
        </div>

        {/* Elemento com Maior Bloqueio */}
        {resultado !== 'equilibrado' && 'bgColor' in content && (
          <div className={`rounded-3xl shadow-xl p-6 md:p-12 mb-6 md:mb-8 border ${content.borderColor}`} style={{ backgroundColor: '#16161a' }}>
            <div className="text-center mb-6 px-4">
              <div className="text-5xl md:text-7xl mb-4">{content.emoji}</div>
              <h2 className="font-bold mb-3" style={{ fontFamily: 'Georgia, serif', fontSize: 'clamp(22px, 5.5vw, 36px)', color: '#C9A96E' }}>
                Elemento com Maior Bloqueio: {content.title}
              </h2>
              <p className="text-gray-300 leading-relaxed">
                {content.description}
              </p>
            </div>

            <div className="space-y-4 md:space-y-6 px-4">
              <div className="rounded-2xl p-5 md:p-6 shadow-md border border-gray-700" style={{ backgroundColor: '#1c1c21' }}>
                <h3 className="font-bold text-white mb-4 flex items-center gap-2">
                  ⚠️ Como Esse Bloqueio Aparece na Sua Vida
                </h3>
                {'impactIntro' in content && content.impactIntro ? (
                  <p className="text-gray-300 leading-relaxed mb-4">{content.impactIntro}</p>
                ) : null}
                <ul className="space-y-3">
                  {content.impacts?.map((impact, index) => (
                    <li key={index} className="flex items-start gap-3 text-gray-300">
                      <span className="text-xl mt-0.5 flex-shrink-0" style={{ color: '#C9A96E' }}>✓</span>
                      <span className="leading-relaxed">{impact}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rounded-2xl p-5 md:p-6 shadow-md border border-gray-700" style={{ backgroundColor: '#1c1c21' }}>
                <h3 className="font-bold text-white mb-4 flex items-center gap-2">
                  🔄 O Que Acontece Se Nada For Ajustado
                </h3>
                {'consequenceIntro' in content && content.consequenceIntro ? (
                  <p className="text-gray-300 leading-relaxed mb-4">{content.consequenceIntro}</p>
                ) : null}
                <ul className="space-y-3">
                  {content.consequences?.map((consequence, index) => (
                    <li key={index} className="flex items-start gap-3 text-gray-300">
                      <span className="text-red-500 font-bold flex-shrink-0">–</span>
                      <span className="leading-relaxed">{consequence}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rounded-2xl p-5 md:p-6 shadow-md border border-gray-700" style={{ backgroundColor: '#1c1c21' }}>
                <h3 className="font-bold text-white mb-4 flex items-center gap-2">
                  ✨ Quando o Elemento {content.title} é Reequilibrado
                </h3>
                {'benefitIntro' in content && content.benefitIntro ? (
                  <p className="text-gray-300 leading-relaxed mb-4">{content.benefitIntro}</p>
                ) : null}
                <ul className="space-y-3">
                  {content.benefits?.map((benefit, index) => (
                    <li key={index} className="flex items-start gap-3 text-gray-300">
                      <span className="text-xl mt-0.5 flex-shrink-0" style={{ color: '#C9A96E' }}>✓</span>
                      <span className="leading-relaxed">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rounded-2xl p-5 md:p-6 shadow-md border border-amber-600/40" style={{ backgroundColor: '#1c1c21' }}>
                <p className="text-white font-semibold leading-relaxed mb-4">Essas dores têm nome. E têm caminho.</p>
                <p className="text-gray-300 leading-relaxed mb-4">
                  O Mapa de Cura dos <span className="numerals">4</span> Elementos será criado para revelar com precisão o que está operando dentro de você. Não de forma genérica, mas a partir do que é específico da sua história, dos seus padrões e das suas dores.
                </p>
                <p className="text-gray-300 leading-relaxed mb-6">
                  Você preenche um questionário com tudo que está incomodando a sua vida. E recebe um documento escrito exatamente para você, com suas dores nomeadas, suas crenças limitantes reveladas e o passo a passo para liberar e elevar.
                </p>
                <motion.a
                  href="https://pay.hotmart.com/V105026857E"
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  className="reative-cta block w-full font-bold rounded-2xl shadow-2xl transition-all duration-300 text-center text-gray-900"
                  style={{ minHeight: '52px', lineHeight: '1.2', backgroundColor: '#f5a623' }}>
                  Quero meu Mapa de Cura dos <span className="numerals">4</span> Elementos
                </motion.a>
              </div>
            </div>
          </div>
        )}

        {/* Mensagem de Equilíbrio */}
        {resultado === 'equilibrado' && (
          <div className="rounded-3xl shadow-xl p-6 md:p-12 mb-6 md:mb-8 border border-green-700" style={{ backgroundColor: '#16161a' }}>
            <div className="text-center mb-6">
              <div className="text-5xl md:text-7xl mb-6">🌟</div>
              <h2 className="font-bold text-white mb-6" style={{ fontFamily: 'Georgia, serif', fontSize: 'clamp(24px, 6vw, 36px)' }}>
                Seus elementos aparecem equilibrados neste momento.
              </h2>
            </div>
            <div className="space-y-4 text-gray-300 leading-relaxed px-4" style={{ fontSize: 'clamp(17px, 4vw, 18px)' }}>
              <p>De acordo com as suas respostas, seus <span className="numerals">4</span> elementos não apresentaram bloqueios detectáveis agora.</p>
              <p className="font-semibold text-white">Mas há algo importante que precisa ser dito.</p>
              <p>Existem padrões muito profundos que nem sempre são percebidos de forma consciente, especialmente quando a pessoa está acostumada a viver com aquele peso há tanto tempo que ele já parece normal. O que foi sempre assim parece que é assim que deve ser.</p>
              <p>Se você vive algum desequilíbrio, quer seja na prosperidade, nas relações, nas emoções, na saúde ou no propósito, saiba que isso não acontece por acaso. Existe uma raiz. E ela pode estar num elemento que você ainda não consegue enxergar sozinho.</p>
              <p className="font-semibold text-white">Observe com atenção a tabela abaixo. Veja o que cada elemento bloqueado costuma indicar na vida de uma pessoa. Sinta se algum desses padrões ressoa com algo que você vive ou já viveu.</p>
              <p className="italic font-semibold" style={{ color: '#C9A96E' }}>A cura começa no momento em que você se permite ver.</p>
              <p className="text-right font-bold text-white mt-6">Mônica Maria</p>
            </div>
          </div>
        )}

        {/* Texto Ponte */}
        <div className="rounded-3xl shadow-xl p-6 md:p-12 mb-6 md:mb-8" style={{ backgroundColor: '#16161a', border: '1px solid rgba(201, 169, 110, 0.28)' }}>
          <h2 className="font-bold leading-relaxed text-center mb-4 md:mb-6" style={{ color: '#C9A96E' }}>
            Você acabou de identificar o que está bloqueado.
          </h2>
          <p className="text-gray-300 leading-relaxed mb-4 italic">
            Mas reconhecer o bloqueio é só o começo. A raiz do que prende sua vida raramente está em um único elemento e raramente tem uma causa visível. Existe algo mais profundo operando dentro de você, crenças antigas, padrões herdados, dores não resolvidas que continuam criando os mesmos ciclos mesmo depois de tudo que você já tentou.
          </p>
          <p className="text-gray-300 leading-relaxed mb-4 italic">
            Enquanto essa raiz não for revelada e trabalhada, os ciclos continuam. O elemento se reequilibra por um tempo e volta a bloquear. A vida avança um pouco e estagna de novo.
          </p>
          <p className="text-gray-300 leading-relaxed mb-4 italic">
            Revelar essa raiz com clareza é o primeiro passo real para mudar de verdade.
          </p>
          <p className="text-gray-300 leading-relaxed mb-4 italic">
            E ter um caminho prático e personalizado para trabalhar isso todos os dias é o que transforma o entendimento em uma nova realidade.
          </p>
          <p className="font-bold leading-relaxed text-center italic" style={{ color: '#C9A96E' }}>
            É exatamente isso que o Mapa de Cura dos <span className="numerals">4</span> Elementos faz por você.
          </p>
        </div>

        {/* Apresentação de Mônica Maria */}
        <div className="rounded-3xl shadow-xl p-6 md:p-12 mb-6 md:mb-8 border border-amber-600/40" style={{ backgroundColor: '#16161a' }}>
          <div className="flex justify-center mb-6 md:mb-8">
            <div
              className="rounded-3xl border-4 border-amber-500 shadow-2xl overflow-hidden flex items-center justify-center"
              style={{ width: '220px', height: '220px', backgroundColor: '#16161a' }}
            >
              <img
                src="https://static.lumi.new/66/66a59164029fe3389411bfef927c8644.webp"
                alt="Mônica Maria"
                className="w-full h-full object-contain"
              />
            </div>
          </div>
          <p className="text-gray-300 leading-relaxed mb-4 px-4 text-center">
            Sou Mônica Maria, Terapeuta Vibracional, criadora da Metodologia Trindade de Cura e do Método Reacenda sua Coroa. Mais de <span className="numerals">15</span> cursos lançados e milhares de pessoas transformadas. Todo o meu trabalho é revelar com profundidade o que ainda te prende e te mostrar quem você realmente é.
          </p>
          <p className="text-gray-300 leading-relaxed px-4 text-center">
            O Mapa de Cura dos <span className="numerals">4</span> Elementos se torna um espelho do seu estado interior e te ensina uma prática poderosa para cocriar e viver a vida que você merece. Um mapa emocionante que ao ler toca profundamente, gerando transformação imediata da sua vibração, consciência e ser.
          </p>
        </div>

        {/* Apresentação do Produto */}
        <div className="rounded-3xl shadow-2xl p-6 md:p-12 mb-6 md:mb-8 border border-amber-600/50" style={{ backgroundColor: '#16161a' }}>
          <div className="text-center mb-6 md:mb-8 px-4">
            <h2 className="font-bold text-amber-400 mb-3" style={{ fontFamily: 'Georgia, serif', fontSize: 'clamp(28px, 7vw, 48px)' }}>
              Mapa de Cura dos <span className="numerals">4</span> Elementos
            </h2>
            <p className="text-gray-400 italic font-semibold" style={{ fontSize: 'clamp(18px, 5vw, 24px)' }}>
              Individual e Personalizado
            </p>
          </div>
          <p className="text-gray-300 leading-relaxed mb-4 px-4">
            Você preenche um questionário com tudo que está incomodando a sua vida, com honestidade e profundidade. E recebe um documento escrito exatamente para você, nomeando as dores, as crenças limitantes e os padrões que estão bloqueando a sua prosperidade, seus relacionamentos e sua evolução.
          </p>
          <p className="text-white leading-relaxed font-semibold text-center px-4">
            Não é genérico. Não é igual para todo mundo. É sobre você, para você e com você.
          </p>
        </div>

        {/* O que você recebe */}
        {resultado !== 'equilibrado' && (
          <div className="rounded-3xl shadow-2xl p-6 md:p-12 mb-6 md:mb-8 border border-gray-700" style={{ backgroundColor: '#16161a' }}>
            <h2 className="font-bold text-white mb-8 md:mb-10 text-center" style={{ fontFamily: 'Georgia, serif', fontSize: 'clamp(24px, 6vw, 36px)' }}>
              O que você recebe:
            </h2>
            <div className="space-y-4 md:space-y-6">
              {[
                { icon: '📋', title: 'Suas dores reveladas com uma clareza que você nunca teve', desc: 'Pela primeira vez você vai ver com clareza as dores guardadas dentro de você, as crenças limitantes e os padrões que se repetem, impedindo sua prosperidade, seus relacionamentos e sua evolução.' },
                { icon: '✨', title: 'O passo a passo para sentir de verdade cada dor, liberar e elevar seu estado interior', desc: 'Para cada dor que você revelar ao preencher o questionário, você recebe uma sequência personalizada de como sentir de verdade essa dor, como liberá-la da forma correta e como elevar sua vibração para criar a vida que você merece.' },
                { icon: '🪷', title: 'Uma prática simples e diária de 5 minutos para mudar tudo', desc: 'Um protocolo de poucos minutos para realizar ao acordar e antes de dormir, criado a partir do que você quer conquistar. Simples, fácil de fazer todos os dias e poderoso o suficiente para transformar tudo.' },
                { icon: '💫', title: 'Seu roteiro de elevação diária', desc: 'Passo a passo das declarações que vão te ajudar a sair dos pensamentos negativos e criar uma vida diferente, mesmo que hoje você ainda não acredite que isso seja possível.' }
              ].map((item, idx) => (
                <div key={idx} className="rounded-2xl p-5 md:p-6 shadow-md" style={{ backgroundColor: '#1c1c21', border: '1px solid rgba(201, 169, 110, 0.28)' }}>
                  <div className="flex items-start gap-3 md:gap-4">
                    <span className="text-3xl md:text-4xl flex-shrink-0">{item.icon}</span>
                    <div>
                      <h3 className="reative-bonus-title font-bold text-white mb-3">
                        {item.title}
                      </h3>
                      <p className="reative-bonus-text text-gray-300 leading-relaxed">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Programa Eleve e Prospere */}
        {resultado !== 'equilibrado' && (
          <div className="rounded-3xl shadow-xl p-6 md:p-10 mb-6 md:mb-8 text-left" style={{ backgroundColor: '#241a05', border: '1px solid rgba(201, 169, 110, 0.45)' }}>
            <p className="font-bold leading-relaxed mb-3" style={{ fontFamily: 'Georgia, serif', fontSize: 'clamp(20px, 5vw, 26px)', color: '#C9A96E' }}>
              🎁 Bônus especial: Programa Eleve e Prospere, <span className="numerals" style={{ color: '#9ca3af', textDecoration: 'line-through' }}>R$297</span>.
            </p>
            <p className="text-gray-200 leading-relaxed">
              <span className="numerals">14</span> aulas completas para desbloquear os <span className="numerals">4</span> elementos no seu dia a dia e práticas poderosas para elevar sua vibração. Você recebe acesso completo, sem custo adicional.
            </p>
          </div>
        )}

        {/* Depoimentos */}
        <div className="rounded-3xl shadow-2xl p-6 md:p-12 mb-6 md:mb-8 border border-gray-700" style={{ backgroundColor: '#16161a' }}>
          <h2 className="font-bold text-white mb-3 text-center" style={{ fontFamily: 'Georgia, serif', fontSize: 'clamp(24px, 6vw, 36px)' }}>
            O que estão dizendo sobre o Mapa de Cura dos <span className="numerals">4</span> Elementos
          </h2>
          <p className="text-gray-400 italic text-center mb-8">
            Depoimentos reais de mulheres que receberam o mapa.
          </p>
          <div className="testimonial-stack flex flex-col gap-3">
            <div className="testimonial-card rounded-2xl p-2 md:p-5 shadow-md border border-amber-600/40" style={{ backgroundColor: '#1c1c21' }}>
              <div className="testimonial-media rounded-xl mb-2 overflow-hidden p-1 md:p-3" style={{ backgroundColor: '#1a1a1a' }}>
                <img src="https://static.lumi.new/f3/f3618701217d5bbc4783b448a2172a65.webp" alt="Depoimento de Vitória" className="w-full rounded-lg" />
              </div>
              <p className="reative-testimonial font-bold text-center" style={{ color: '#C9A96E' }}>Vitória</p>
            </div>
            <div className="testimonial-card rounded-2xl p-2 md:p-5 shadow-md" style={{ backgroundColor: '#1c1c21', border: '1px solid rgba(201, 169, 110, 0.28)' }}>
              <div className="testimonial-media testimonial-fatima rounded-xl mb-2 overflow-hidden p-1 md:p-3" style={{ backgroundColor: '#1a1a1a' }}>
                <img src="https://static.lumi.new/c4/c4191e854efdbe14dbd34b3ec57cd45f.webp" alt="Depoimento de Fátima Verona" className="w-full rounded-lg" />
              </div>
              <p className="reative-testimonial font-bold text-center" style={{ color: '#C9A96E' }}>Fátima Verona</p>
            </div>
          </div>
        </div>

        {/* Preço e Botão */}
        <div className="rounded-3xl shadow-2xl p-6 md:p-12 mb-6 md:mb-8 border border-amber-500" style={{ backgroundColor: '#1a1200' }}>
          <div className="text-center mb-6 md:mb-8 px-4">
            <p className="text-gray-300 leading-relaxed mb-6 md:mb-8">
              Este mapa será criado especificamente para você. Para cada dor que você revelar, você recebe o caminho de como senti-la de verdade, como liberá-la da forma correta e como elevar sua vibração para criar a vida que você merece.
            </p>
            <div className="rounded-2xl p-5 md:p-7 mb-6 md:mb-8 text-left" style={{ backgroundColor: '#241a05', border: '1px solid rgba(201, 169, 110, 0.4)' }}>
              <p className="font-bold leading-relaxed" style={{ fontFamily: 'Georgia, serif', fontSize: 'clamp(20px, 5vw, 26px)', color: '#C9A96E' }}>
                🎯 Bônus por tempo limitado. Ao adquirir o Mapa de Cura dos <span className="numerals">4</span> Elementos hoje, você ganha <span className="numerals">1</span> atendimento individual de <span className="numerals">30</span> minutos comigo para liberar ao vivo o seu maior bloqueio.
              </p>
            </div>
            <div className="mb-6 md:mb-8">
              <p className="font-bold text-amber-400 mb-2 numerals reative-price" style={{ fontFamily: 'Georgia, serif' }}>R$97</p>
              <p className="text-amber-300 font-semibold mb-4 numerals reative-price-parcel">
                ou 6x de R$18,20 no cartão
              </p>
              <p className="text-gray-400 italic">
                Acesso imediato após a compra.
              </p>
            </div>
          </div>
          <motion.a
            href="https://pay.hotmart.com/V105026857E"
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            className="reative-cta block w-full font-bold rounded-2xl shadow-2xl transition-all duration-300 text-center text-gray-900"
            style={{ minHeight: '52px', lineHeight: '1.2', backgroundColor: '#f5a623' }}>
            Quero meu Mapa de Cura dos <span className="numerals">4</span> Elementos
          </motion.a>
        </div>

        {/* Como Funciona Após a Compra */}
        <div className="rounded-3xl shadow-xl p-6 md:p-10 mb-6 md:mb-8 border border-gray-700" style={{ backgroundColor: '#16161a' }}>
          <h3 className="font-bold text-white mb-4 text-center" style={{ fontFamily: 'Georgia, serif', fontSize: 'clamp(20px, 5vw, 28px)' }}>
            Como Funciona Após a Compra
          </h3>
          <div className="space-y-4 text-gray-300 leading-relaxed px-4">
            <p>
              Assim que você confirmar seu pedido, receberá no WhatsApp o link do formulário para preencher com suas respostas. O formulário é a base de tudo. É a partir do que você compartilhar que o seu Mapa será criado.
            </p>
            <p>
              Após o preenchimento, seu Mapa de Cura dos <span className="numerals">4</span> Elementos personalizado será entregue em até <span className="numerals">72</span> horas no seu e-mail.
            </p>
            <p className="font-semibold text-white text-center italic">
              Não é genérico. Não é automático. É criado especificamente para o que você revelou.
            </p>
          </div>
        </div>

        {/* Garantia */}
        <div className="rounded-3xl shadow-xl p-6 md:p-10 mb-6 md:mb-8 border border-green-700/50 text-center" style={{ backgroundColor: '#0a1a0e' }}>
          <div className="text-4xl md:text-5xl mb-4">🛡️</div>
          <h3 className="font-bold text-white mb-4" style={{ fontFamily: 'Georgia, serif', fontSize: 'clamp(20px, 5.5vw, 28px)' }}>
            Garantia Incondicional de <span className="numerals">7</span> Dias
          </h3>
          <p className="reative-warranty text-gray-300 leading-relaxed mb-3 px-4">
            Se você receber o seu mapa e sentir que não foi o que esperava, basta me enviar uma mensagem e devolvo <span className="numerals">100%</span> do seu investimento.
          </p>
          <p className="reative-warranty text-gray-300 leading-relaxed px-4">
            Sem perguntas. Sem burocracia.
          </p>
        </div>

        {/* Fechamento */}
        <div className="rounded-3xl shadow-xl p-6 md:p-10 mb-6 md:mb-8 text-center" style={{ backgroundColor: '#16161a', border: '1px solid rgba(201, 169, 110, 0.28)' }}>
          <p className="text-gray-300 leading-relaxed italic mb-6 px-4" style={{ fontSize: 'clamp(18px, 4.5vw, 20px)' }}>
            Você já sentiu que algo precisa mudar. O Mapa é o primeiro passo real para liberar o que está bloqueando a sua vida.
          </p>
          <div className="border-t border-gray-700 pt-6 mt-6 px-4">
            <p className="font-bold text-white mb-2" style={{ fontFamily: 'Georgia, serif', fontSize: 'clamp(20px, 5.5vw, 28px)' }}>
              Mônica Maria
            </p>
            <p style={{ fontSize: 'clamp(18px, 4.5vw, 20px)', color: '#C9A96E' }}>
              Reacenda Luz
            </p>
          </div>
        </div>

        {/* Rodapé Final */}
        <div className="reative-footer text-center text-gray-600 border-t border-gray-800 pt-6 px-4">
          <p className="mb-2">Material exclusivo e protegido.</p>
          <p className="font-bold mb-2">Mônica Maria, Reacenda Luz.</p>
          <p>Proibida a reprodução sem autorização.</p>
        </div>
      </motion.div>
    </div>
  );
}
